import React, { useState, useEffect } from "react";
import "../page/addProductInvoice.css";

import { Constant } from "../../../helper/constant";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { formatRupiah } from "../../../helper/formatNumber";

const EditProductInvoice = () => {
  // =====================
  // Constant Definition
  // =====================

  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { productData } = location.state || {};
  const [selectedProductData, setSelectedProductData] = useState(productData);
  const selectedSupplierTaxStatus =
    productData.product_invoice_supplier.supplier_tax_status;

  // =====================
  // Function Definition
  // =====================

  const formatDate = (date, isAsValue) => {
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    };

    const result = new Intl.DateTimeFormat("id-ID", options).format(date);
    if (isAsValue) {
      const [day, month, year] = result.split("/");
      return `${year}-${month}-${day}`;
    }
    return result;
  };

  const convertStringToDate = (dateString) => {
    var dateParts = dateString.split("/");
    var dateObject = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
    return dateObject;
  };

  const backToPreviousPage = () => {
    setSelectedProductData({
      product_invoice_id: "",
      product_invoice_supplier_id: "",
      product_invoice_document_number: "",
      product_invoice_date: new Date(),
      product_invoice_due_date: new Date(),
      product_invoice_products: [],
      product_invoice_total_discount: 0,
      product_invoice_total_order: 0,
      product_invoice_total_tax: 0,
      product_invoice_total_netto: 0,
      product_invoice_total_netto_tax: 0,
    });
    navigate("/produk-invoice", { replace: true });
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = selectedProductData.product_invoice_products.map(
      (product, i) => {
        if (i === index) {
          if (
            field === "product_invoice_product_price_per_unit" ||
            field === "product_invoice_product_quantity" ||
            field === "product_invoice_product_discount"
          ) {
            const updatedFieldValue =
              field === "product_invoice_product_price_per_unit"
                ? { product_invoice_product_price_per_unit: value }
                : field === "product_invoice_product_quantity"
                ? { product_invoice_product_quantity: value }
                : { product_invoice_product_discount: value };

            const pricePerUnit =
              field === "product_invoice_product_price_per_unit"
                ? value
                : product.product_invoice_product_price_per_unit;

            const quantity =
              field === "product_invoice_product_quantity"
                ? value
                : product.product_invoice_product_quantity;

            const discount =
              field === "product_invoice_product_discount"
                ? value
                : product.product_invoice_product_discount;

            const totalPrice = Math.ceil(
              pricePerUnit * quantity -
                pricePerUnit * quantity * (discount / 100)
            );

            return {
              ...product,
              ...updatedFieldValue,
              product_invoice_total_product_price: totalPrice,
            };
          } else {
            return {
              ...product,
              [field]: value,
            };
          }
        }
        return product;
      }
    );

    setSelectedProductData((prevData) => ({
      ...prevData,
      product_invoice_products: updatedProducts,
    }));
  };

  // =====================
  // Request Method
  // =====================

  const editProduct = async () => {
    try {
      const invoiceProducts = selectedProductData.product_invoice_products.map(
        ({ _id, product_data, ...rest }) => rest
      );

      const response = await fetch(
        Constant.BASE_API_URL +
          "/invoice-produk/" +
          selectedProductData.product_invoice_id,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            product_invoice_supplier_id:
              selectedProductData.product_invoice_supplier.supplier_id,
            product_invoice_supplier_name:
              selectedProductData.product_invoice_supplier.supplier_name,
            product_invoice_document_number:
              selectedProductData.product_invoice_document_number,
            product_invoice_date: selectedProductData.product_invoice_date,
            product_invoice_due_date:
              selectedProductData.product_invoice_due_date,
            product_invoice_products: invoiceProducts,
            product_invoice_total_discount:
              selectedProductData.product_invoice_total_discount,
            product_invoice_total_order:
              selectedProductData.product_invoice_total_order,
            product_invoice_total_tax:
              selectedProductData.product_invoice_total_tax,
            product_invoice_total_netto:
              selectedProductData.product_invoice_total_netto,
            product_invoice_total_netto_tax:
              selectedProductData.product_invoice_total_netto_tax,
          }),
        }
      );

      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      } else {
        backToPreviousPage();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Compute updated totals
    const updatedTotals = selectedProductData.product_invoice_products.reduce(
      (totals, product) => {
        const {
          product_invoice_product_quantity: quantity,
          product_invoice_product_price_per_unit: pricePerUnit,
          product_invoice_product_discount: discountPercentage,
        } = product;

        const totalPrice = quantity * pricePerUnit;
        const discount = (totalPrice * discountPercentage) / 100;

        totals.totalOrder += totalPrice;
        totals.totalDiscount += discount;

        return totals;
      },
      { totalOrder: 0, totalDiscount: 0 }
    );

    const totalNetto = updatedTotals.totalOrder - updatedTotals.totalDiscount;
    var totalTax = 0;
    var totalNettoTax = 0;
    if (selectedSupplierTaxStatus === "PKP") {
      totalTax = Math.ceil(totalNetto * 0.11);
      totalNettoTax = totalNetto + totalTax;
    }

    setSelectedProductData((prevData) => ({
      ...prevData,
      product_invoice_total_order: updatedTotals.totalOrder,
      product_invoice_total_discount: updatedTotals.totalDiscount,
      product_invoice_total_netto: totalNetto,
      product_invoice_total_tax: totalTax,
      product_invoice_total_netto_tax: totalNettoTax,
    }));
  }, [selectedProductData.product_invoice_products, selectedSupplierTaxStatus]);

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1 className="header-invoice">Edit Faktur</h1>
      <div className="invoice-document-header">
        <input
          className="document-number-text-field"
          type="text"
          value={
            selectedProductData.product_invoice_document_number
              ? selectedProductData.product_invoice_document_number
              : ""
          }
          onChange={(e) =>
            setSelectedProductData({
              ...selectedProductData,
              product_invoice_document_number: e.target.value,
            })
          }
          placeholder="Masukkan nomor dokumen"
        />
        <div className="supplier-container">
          <input
            className="supplier-text-field"
            type="text"
            value={
              selectedProductData.product_invoice_supplier.supplier_name || ""
            }
            placeholder="Masukkan nama Supplier"
            disabled
          />
        </div>
        <div className="input-title-label-container">
          <span className="input-title-label">Tanggal</span>
        </div>
        <div className="input-patient-field-container">
          <input
            type="date"
            value={
              selectedProductData.product_invoice_date
                ? formatDate(
                    convertStringToDate(
                      selectedProductData.product_invoice_date
                    ),
                    true
                  )
                : formatDate(new Date(), true)
            }
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              if (!isNaN(selectedDate.getTime())) {
                const formattedDate = formatDate(selectedDate, false);
                setSelectedProductData({
                  ...selectedProductData,
                  product_invoice_date: formattedDate,
                });
              }
            }}
            onInput={(e) => e.preventDefault()}
            placeholder="Masukkan Tanggal invoice"
          />
        </div>
        <div className="input-title-label-container">
          <span className="input-title-label">Jatuh Tempo</span>
        </div>
        <div className="input-patient-field-container">
          <input
            type="date"
            value={
              selectedProductData.product_invoice_due_date
                ? formatDate(
                    convertStringToDate(
                      selectedProductData.product_invoice_due_date
                    ),
                    true
                  )
                : formatDate(new Date(), true)
            }
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              if (!isNaN(selectedDate.getTime())) {
                const formattedDate = formatDate(selectedDate, false);
                setSelectedProductData({
                  ...selectedProductData,
                  product_invoice_due_date: formattedDate,
                });
              }
            }}
            onInput={(e) => e.preventDefault()}
            placeholder="Masukkan Tanggal invoice"
          />
        </div>
      </div>
      <div className="section-product-invoice">
        <table className="product-invoice-table">
          <thead>
            <tr>
              <th>Nama Produk</th>
              <th>Tanggal Kadaluwarsa</th>
              <th>Nomor Batch</th>
              <th>Kuantitas</th>
              <th>Harga Satuan</th>
              <th>Diskon (%)</th>
              <th>Total Harga</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {selectedProductData.product_invoice_products.map(
              (product, index) => (
                <tr key={index}>
                  <td>
                    <input
                      className="product-name"
                      type="text"
                      value={
                        product.product_invoice_product_name
                          ? product.product_invoice_product_name
                          : ""
                      }
                      placeholder="Masukkan nama product"
                      disabled
                    />
                  </td>
                  <td>
                    <input
                      className="product-expired-date"
                      type="date"
                      value={formatDate(
                        convertStringToDate(
                          product.product_invoice_product_expired_date
                        ),
                        true
                      )}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        if (!isNaN(selectedDate.getTime())) {
                          const formattedDate = formatDate(selectedDate, false);
                          handleProductChange(
                            index,
                            "product_invoice_product_expired_date",
                            formattedDate
                          );
                        } else {
                          console.error("Invalid date value:", e.target.value);
                        }
                      }}
                      onInput={(e) => e.preventDefault()}
                      placeholder="Masukkan Tanggal invoice"
                    />
                  </td>

                  <td>
                    <input
                      className="product-batch-number"
                      type="text"
                      value={
                        product.product_invoice_product_batch
                          ? product.product_invoice_product_batch
                          : ""
                      }
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "product_invoice_product_batch",
                          e.target.value
                        )
                      }
                      placeholder="Nomor Batch"
                    />
                  </td>
                  <td>
                    <input
                      className="product-quantity"
                      type="text"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      }}
                      value={
                        product.product_invoice_product_quantity
                          ? product.product_invoice_product_quantity
                          : ""
                      }
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "product_invoice_product_quantity",
                          e.target.value
                        )
                      }
                      placeholder="kuantitas"
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                  <td>
                    <input
                      className="product-unit-price"
                      type="text"
                      value={
                        selectedProductData.product_invoice_products[index]
                          .product_invoice_product_price_per_unit
                          ? selectedProductData.product_invoice_products[index]
                              .product_invoice_product_price_per_unit
                          : ""
                      }
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      }}
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "product_invoice_product_price_per_unit",
                          e.target.value
                        )
                      }
                      placeholder="Harga satuan produk"
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                  <td>
                    <input
                      className="product-discount"
                      type="text"
                      value={
                        selectedProductData.product_invoice_products[index]
                          .product_invoice_product_discount
                          ? selectedProductData.product_invoice_products[index]
                              .product_invoice_product_discount
                          : ""
                      }
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9.]/g, ""); // Allow numbers and a single dot
                      }}
                      onChange={(e) => {
                        let value = e.target.value;
                        let dotCount = (value.match(/\./g) || []).length;
                        if (dotCount > 1) {
                          value = value.slice(0, value.lastIndexOf("."));
                        }
                        let parts = value.split(".");
                        parts[0] = parts[0].replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          "."
                        ); // Add dots for thousands

                        let formattedValue = parts.join(".");

                        handleProductChange(
                          index,
                          "product_invoice_product_discount",
                          formattedValue
                        );
                      }}
                      placeholder="Diskon"
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                  <td>
                    <input
                      className="product-total-price"
                      type="text"
                      disabled={true}
                      value={
                        selectedProductData.product_invoice_products[index]
                          .product_invoice_total_product_price
                          ? formatRupiah(
                              Number(
                                selectedProductData.product_invoice_products[
                                  index
                                ].product_invoice_total_product_price
                              )
                            )
                          : ""
                      }
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "product_invoice_total_product_price",
                          e.target.value
                        )
                      }
                      placeholder="Total Harga"
                    />
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      <div style={{ margin: 16 }}>
        <div className="sales-summary-container">
          <h3>Detail Pembelian</h3>
          <div className="sales-summary-transaction-table">
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Total Order :</p>
              <p className="sales-summary-column">
                <b>
                  {formatRupiah(
                    selectedProductData.product_invoice_total_order
                  )}
                </b>
              </p>
            </div>
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Diskon Rp :</p>
              <p className="sales-summary-column">
                <b>
                  {formatRupiah(
                    selectedProductData.product_invoice_total_discount
                  )}
                </b>
              </p>
            </div>
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Netto :</p>
              <p className="sales-summary-column">
                <b>
                  {formatRupiah(
                    selectedProductData.product_invoice_total_netto
                  )}
                </b>
              </p>
            </div>
            {selectedSupplierTaxStatus === "PKP" && (
              <div>
                <div className="sales-summary-row">
                  <p className="sales-summary-column-title">PPN :</p>
                  <p className="sales-summary-column">
                    <b>
                      {formatRupiah(
                        selectedProductData.product_invoice_total_tax
                      )}
                    </b>
                  </p>
                </div>
                <div className="sales-summary-row">
                  <p className="sales-summary-column-title">Netto PPN :</p>
                  <p className="sales-summary-column">
                    <b>
                      {formatRupiah(
                        selectedProductData.product_invoice_total_netto_tax
                      )}
                    </b>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ margin: 16 }}>
        <footer className="modal-product-footer">
          <button className="modal-close-button" onClick={backToPreviousPage}>
            Batal
          </button>

          <button
            className="modal-submit-button"
            onClick={() => {
              setIsLoading(true);
              editProduct();
            }}
          >
            Submit
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EditProductInvoice;
